import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import { getKaajSectionSpacing } from "../../../../modules/layout/kaaj-section-spacing";
import { RichTextHtml } from "../../RichTextHtml";
import { KaajAction } from "../_shared/KaajAction";
import type { ITestimonialGridBlockProps, TestimonialGridItem } from "./TestimonialGridBlock.props";

const KaajImage = resolveKaajPrimitive("Image");
const KaajLink = resolveKaajPrimitive("Link");

const Rating = ({ value }: { value?: number | null }) => {
  const rating = value;

  if (rating == null) return null;

  return (
    <div
      aria-label={`${rating} out of 5 star rating`}
      className="flex items-center gap-1.5"
      role="img"
    >
      {Array.from({ length: 5 }).map((_, index) => (
        <span
          aria-hidden="true"
          className={cn(
            "text-[0.95rem] leading-none drop-shadow-[0_3px_8px_rgba(245,184,75,0.22)]",
            index < rating ? "text-[#E9A72E]" : "text-[#CBD6FF]",
          )}
          key={index}
        >
          ★
        </span>
      ))}
    </div>
  );
};

const getSafeRel = (href?: string | null, target?: string | null) => {
  const external = href?.startsWith("http") || href?.startsWith("//");

  return target === "_blank" || external ? "noopener noreferrer" : undefined;
};

const getGridLayoutClassName = (itemCount: number) => {
  if (itemCount === 1) {
    return "max-w-[38rem]";
  }

  if (itemCount === 2 || itemCount === 4) {
    return "md:grid-cols-2";
  }

  return "w-full md:grid-cols-2 xl:grid-cols-3";
};

const TestimonialLogo = ({ index, item }: { index: number; item: TestimonialGridItem }) => {
  if (!item.logo) return null;

  return (
    <div className="flex h-10 max-w-[8.75rem] shrink-0 items-center justify-center rounded-[1.15rem] border border-[#D9E1FF] bg-white px-3 py-2 shadow-[0_10px_24px_rgba(33,68,255,0.06)]">
      <KaajImage
        alt={item.logo.alternateText}
        className="max-h-6 w-auto max-w-full object-contain opacity-85 saturate-0 transition duration-300 group-hover/card:opacity-100 group-hover/card:saturate-100 motion-reduce:transition-none"
        decoding="async"
        height={item.logo.height ?? undefined}
        loading={index < 3 ? "eager" : "lazy"}
        priority={index < 3}
        src={item.logo.defaultSrc}
        srcSet={item.logo.srcSet ?? undefined}
        width={item.logo.width ?? undefined}
      />
    </div>
  );
};

const TestimonialAvatar = ({ index, item }: { index: number; item: TestimonialGridItem }) => {
  if (!item.avatar) return null;

  return (
    <div className="relative size-12 shrink-0 overflow-hidden rounded-full border border-[#CAD7FF] bg-[radial-gradient(circle_at_28%_18%,#FFEAF9,transparent_34%),linear-gradient(135deg,#EEF2FF,#D9E1FF)] p-1 shadow-[0_10px_26px_rgba(33,68,255,0.10)]">
      <KaajImage
        alt={item.avatar.alternateText}
        className="h-full w-full rounded-full bg-[#EFF3FF] object-cover"
        decoding="async"
        height={item.avatar.height ?? undefined}
        loading={index < 2 ? "eager" : "lazy"}
        priority={index < 2}
        src={item.avatar.defaultSrc}
        srcSet={item.avatar.srcSet ?? undefined}
        style={
          item.avatar.focalPoint
            ? { objectPosition: `${item.avatar.focalPoint.x}% ${item.avatar.focalPoint.y}%` }
            : undefined
        }
        width={item.avatar.width ?? undefined}
      />
    </div>
  );
};

const StoryLink = ({ item }: { item: TestimonialGridItem }) => {
  if (!item.link?.href || !item.link.text) return null;

  return (
    <KaajLink
      aria-label={item.link.ariaLabel ?? undefined}
      className="inline-flex min-h-11 max-w-full items-center justify-center rounded-full border border-[#D9E1FF] bg-[#FAFBFF] px-4 py-2 text-left text-sm font-extrabold leading-tight text-[#2144FF] shadow-[0_10px_24px_rgba(33,68,255,0.06)] transition-[background-color,border-color,color,transform] duration-200 hover:border-[#2144FF] hover:bg-[#F6F1FF] hover:text-[#132EDB] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2144FF] motion-reduce:transition-none motion-safe:hover:-translate-y-0.5 max-[420px]:w-full"
      href={item.link.href}
      rel={getSafeRel(item.link.href, item.link.target)}
      target={item.link.target ?? undefined}
    >
      <span className="min-w-0 [overflow-wrap:anywhere]">{item.link.text}</span>
      <span aria-hidden="true" className="ml-2 shrink-0">
        →
      </span>
    </KaajLink>
  );
};

const TestimonialCard = ({
  className,
  index,
  item,
  itemClassName,
}: {
  className?: string;
  index: number;
  item: TestimonialGridItem;
  itemClassName?: string;
}) => {
  const meta = [item.role, item.company].filter(Boolean).join(" · ");
  const hasRating = item.rating != null;
  const hasTopMatter = hasRating || item.logo;
  const hasIdentity = Boolean(item.personName || meta || item.avatar || item.link?.href);

  return (
    <figure
      className={cn(
        "group/card relative isolate flex h-full min-h-[20rem] min-w-0 flex-col overflow-hidden rounded-[1.85rem] border border-[#D9E1FF] bg-white p-5 text-[#151927] shadow-[0_18px_52px_rgba(33,68,255,0.08)] transition-[border-color,box-shadow,transform] duration-300 motion-reduce:transition-none motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-[0_26px_72px_rgba(33,68,255,0.13)] sm:p-6 lg:p-7",
        className,
        itemClassName,
      )}
      role="listitem"
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-3 top-1 select-none text-[7rem] font-extrabold leading-none tracking-[-0.08em] text-[#D9E1FF]/45"
      >
        “
      </span>

      {hasTopMatter ? (
        <div className="relative flex min-w-0 items-center justify-between gap-4">
          {hasRating ? <Rating value={item.rating} /> : <span aria-hidden="true" />}
          <TestimonialLogo index={index} item={item} />
        </div>
      ) : null}

      {item.quote ? (
        <blockquote
          className={cn(
            "relative text-pretty text-[1.05rem] font-bold leading-7 tracking-[-0.025em] text-[#253457] [overflow-wrap:anywhere]",
            hasTopMatter ? "mt-6" : "mt-0",
          )}
        >
          <p>{item.quote}</p>
        </blockquote>
      ) : null}

      {hasIdentity ? (
        <figcaption className="relative mt-auto pt-7">
          <div
            aria-hidden="true"
            className="mb-5 h-px w-full bg-gradient-to-r from-[#D9E1FF] via-[#D9E1FF] to-transparent"
          />
          <div className="flex min-w-0 flex-col items-start gap-4">
            <div className="flex min-w-0 items-center gap-3.5">
              <TestimonialAvatar index={index} item={item} />
              <div className="min-w-0 flex-1">
                {item.personName ? (
                  <p className="text-base font-extrabold leading-6 text-[#151927] [overflow-wrap:anywhere]">
                    {item.personName}
                  </p>
                ) : null}
                {meta ? (
                  <p className="mt-1 text-sm font-medium leading-6 text-[#4B5568] [overflow-wrap:anywhere]">
                    {meta}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="min-w-0">
              <StoryLink item={item} />
            </div>
          </div>
        </figcaption>
      ) : null}
    </figure>
  );
};

export const TestimonialGridBlock = (props: ITestimonialGridBlockProps) => {
  const items = props.items ?? [];
  const sectionId = props.anchorId ?? undefined;
  const headingId = props.heading && sectionId ? `${sectionId}-heading` : undefined;
  const hasIntro = Boolean(props.eyebrow || props.heading || props.subheading);

  if (!hasIntro && items.length === 0 && !props.buttons?.length) {
    return null;
  }

  return (
    <section
      className={cn(
        "relative isolate overflow-hidden text-[#151927]",
        getKaajSectionSpacing(props.spacing),
        props.parentClass,
      )}
      data-kaaj-block="testimonialGrid"
      id={sectionId}
      {...(headingId ? { "aria-labelledby": headingId } : {})}
    >
      <div className="kaaj-container relative">
        {hasIntro ? (
          <div
            className={cn(
              "mx-auto mb-9 flex max-w-4xl flex-col items-center gap-4 text-center lg:mb-11",
            )}
          >
            {props.eyebrow ? (
              <p className="inline-flex max-w-full rounded-full border border-[#CAD7FF] bg-white px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.18em] text-[#2144FF] shadow-[0_10px_26px_rgba(33,68,255,0.09)] [overflow-wrap:anywhere]">
                {props.eyebrow}
              </p>
            ) : null}
            {props.heading ? (
              <h2 className="kaaj-section-heading max-w-4xl text-[#151927]" id={headingId}>
                {props.heading}
              </h2>
            ) : null}
            {props.subheading ? (
              <RichTextHtml
                className="mx-auto max-w-2xl text-pretty text-base leading-7 text-[#4B5568] [overflow-wrap:anywhere] sm:text-lg sm:leading-8"
                html={props.subheading}
              />
            ) : null}
          </div>
        ) : null}

        {items.length > 0 ? (
          <div
            className={cn(
              "relative mx-auto grid min-w-0 gap-5 sm:gap-6",
              getGridLayoutClassName(items.length),
            )}
            role="list"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-x-5 -top-8 h-28 rounded-full bg-[radial-gradient(circle_at_18%_42%,rgba(33,68,255,0.10),transparent_34%),radial-gradient(circle_at_78%_45%,rgba(255,234,249,0.48),transparent_34%)] blur-2xl"
            />

            {items.map((item, index) => (
              <TestimonialCard
                index={index}
                item={item}
                key={`${item.personName || item.quote || "testimonial"}-${index}`}
              />
            ))}
          </div>
        ) : null}

        {props.buttons?.length ? (
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {props.buttons.map((button, index) => (
              <KaajAction
                ariaLabel={button.ariaLabel}
                href={button.href}
                key={`${button.href}-${button.text}-${index}`}
                label={button.text}
                target={button.target}
                variant={button.style}
              />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

TestimonialGridBlock.displayName = "Block::TestimonialGridBlock";
